import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // ON: mjukt galleribyte vid färgbyte. Native bytte ut hela <media-gallery> direkt, vilket rev
    // ner och byggde upp alla bilder igen -> blank blink + höjdhopp ("studsar"). Vi
    // (1) hoppar över bytet helt när mediauppsattningen ar identisk (t.ex. storleksbyte),
    // (2) laser hojden under bytet sa layouten inte hoppar,
    // (3) vantar in att forsta bilden ar dekodad innan vi byter, sa den ar malad direkt.
    const currentSignature = this.#mediaSignature(this);
    if (currentSignature && currentSignature === this.#mediaSignature(newMediaGallery)) return;

    // ON (omgång 80): med lifestylebilden först visar ett färgbyte färgens bild (andra bilden),
    // medan lifestylebilden ligger kvar först i galleriet.
    // Bild 2 väljs uttryckligen när det nya galleriet har kopplats in. Inte via initial-slide:
    // bildernas slide-id saknas, och initial-slide satte current = 1 utan att skrolla, så select(1)
    // trodde att bilden redan var vald.
    const lifestyleFirst = newMediaGallery.hasAttribute('data-on-lifestyle-first');

    const lockedHeight = this.offsetHeight;
    const swap = () => {
      if (!this.isConnected) return;
      if (lockedHeight) newMediaGallery.style.minHeight = `${lockedHeight}px`;
      this.replaceWith(newMediaGallery);
      if (lifestyleFirst) {
        const slideshow = newMediaGallery.querySelector('slideshow-component[ref="slideshow"]');
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (slideshow && typeof slideshow.select === 'function') slideshow.select(1, undefined, { animate: false });
          })
        );
      }
      if (lockedHeight) {
        requestAnimationFrame(() => {
          newMediaGallery.style.minHeight = '';
        });
      }
    };

    const firstImage = newMediaGallery.querySelector('img');
    if (!firstImage || firstImage.complete) {
      swap();
      return;
    }

    // Dekodera utanfor dokumentet forst. Timeout-vakt sa ett trasigt bildsvar aldrig later
    // galleriet frysa pa den gamla farfen.
    const preload = new Image();
    if (firstImage.srcset) preload.srcset = firstImage.srcset;
    if (firstImage.sizes) preload.sizes = firstImage.sizes;
    preload.src = firstImage.currentSrc || firstImage.src;

    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      swap();
    };

    setTimeout(once, 300);
    preload.decode().then(once, once);
  };

  /**
   * Bygger en signatur av galleriets media-ordning, sa vi kan avgora om ett byte behovs alls.
   *
   * @param {Element} gallery - Galleriet att signera.
   * @returns {string} Signaturen.
   */
  #mediaSignature(gallery) {
    return Array.from(gallery.querySelectorAll('[data-media-id]'))
      .map((node) => node.getAttribute('data-media-id'))
      .join(',');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
